import { useNavigate, useParams } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { StatusBadge } from '../components/StatusBadge';
import { useTrackingStream } from '../lib/useTrackingStream';
import { UI_COPY, type SupportedLanguage } from '../i18n/uiCopy';

export function TrackPage({ language, setLanguage }: { language: SupportedLanguage; setLanguage: (l: SupportedLanguage) => void }) {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const copy = UI_COPY[language];
  const { data, loading, error, errorStatus, live } = useTrackingStream(code, language);

  return (
    <div className="page">
      <TopBar copy={copy} language={language} onLanguageChange={setLanguage} />
      <main>
        {loading && (
          <div className="card center">
            <div className="spinner" role="status" aria-label={copy.loadingText} />
            <p>{copy.loadingText}</p>
          </div>
        )}

        {!loading && error && (
          <div className="card">
            <div className="banner critical">{errorStatus === 404 ? copy.notFoundError : copy.genericError}</div>
            <button className="link" onClick={() => navigate('/')}>
              {copy.backHome}
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div className="card">
              <div className="status-row">
                <StatusBadge status={data.currentStatus} label={data.currentStatusLabel} />
                <span className="live-indicator">
                  {live && <span className="live-dot" aria-hidden="true" />}
                  {live ? copy.liveIndicator : ''}
                </span>
              </div>
              {data.orderReference && (
                <p className="subheading" style={{ margin: '2px 0 0' }}>
                  {copy.orderReferenceLabel} {data.orderReference} · {data.trackingCode}
                </p>
              )}

              <div className="route-summary">
                <div className="route-point">
                  <div className="caption">{copy.originLabel}</div>
                  <div className="city">{data.origin.city}</div>
                </div>
                <div className="route-arrow" aria-hidden="true">
                  →
                </div>
                <div className="route-point">
                  <div className="caption">{copy.destinationLabel}</div>
                  <div className="city">{data.destination.city}</div>
                </div>
              </div>

              {data.currentLocation && (
                <div className="current-location">
                  <strong>{copy.currentLocationLabel}:</strong> {data.currentLocation.city}, {data.currentLocation.region}
                </div>
              )}

              <p className="subheading" style={{ marginTop: 12, marginBottom: 0, fontSize: '0.78rem' }}>
                {copy.lastUpdatedLabel}: {new Date(data.lastUpdated).toLocaleString(language)}
              </p>
            </div>

            <div className="card">
              <h2>{copy.historyHeading}</h2>
              <ul className="timeline">
                {[...data.events].reverse().map((event, idx) => (
                  <li key={`${event.eventType}-${event.occurredAt}-${idx}`}>
                    <div className="event-label">{event.label}</div>
                    <div className="event-meta">
                      {event.city}
                      {event.region ? `, ${event.region}` : ''} · {new Date(event.occurredAt).toLocaleString(language)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {data.upcoming.length > 0 && (
              <div className="card">
                <h2>{copy.upcomingHeading}</h2>
                <ul className="upcoming-list">
                  {data.upcoming.map((stage) => (
                    <li key={stage.key}>{stage.label}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="center">
              <button className="link" onClick={() => navigate('/')}>
                {copy.backHome}
              </button>
            </div>
          </>
        )}
      </main>
      <footer>{copy.footerNote}</footer>
    </div>
  );
}
